/**
 * Form validation helper functions for the Salzburg Cab authentication modal.
 */

export interface ValidationErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  phone?: string;
  otp?: string;
}

// Simple email regex pattern
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Simple phone regex pattern (allows optional +, spaces, and 7-15 digits)
const PHONE_REGEX = /^\+?[0-9\s\-()]{7,15}$/;

/**
 * Validates an email-only form such as Forgot Password.
 */
export function validateEmailOnly(email: string): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!email.trim()) {
    errors.email = 'Email address is required.';
  } else if (!EMAIL_REGEX.test(email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }

  return errors;
}

/**
 * Validates Sign In Form inputs.
 */
export function validateSignIn(email: string, password: string): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!email.trim()) {
    errors.email = 'Email address is required.';
  } else if (!EMAIL_REGEX.test(email)) {
    errors.email = 'Please enter a valid email address.';
  }

  if (!password) {
    errors.password = 'Password is required.';
  } else if (password.length < 6) {
    errors.password = 'Password must be at least 6 characters.';
  }

  return errors;
}

/**
 * Validates Sign Up Form inputs.
 */
export function validateSignUp(email: string, phone: string): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!email.trim()) {
    errors.email = 'Email address is required.';
  } else if (!EMAIL_REGEX.test(email)) {
    errors.email = 'Please enter a valid email address.';
  }

  if (!phone.trim()) {
    errors.phone = 'Mobile number is required.';
  } else if (!PHONE_REGEX.test(phone)) {
    errors.phone = 'Please enter a valid mobile number (7-15 digits).';
  }

  return errors;
}

/**
 * Validates OTP code.
 */
export function validateOtp(otp: string[]): ValidationErrors {
  const errors: ValidationErrors = {};
  const fullCode = otp.join('');

  if (fullCode.length < 6) {
    errors.otp = 'Please enter the full 6-digit verification code.';
  } else if (!/^\d+$/.test(fullCode)) {
    errors.otp = 'Verification code must contain digits only.';
  }

  return errors;
}

/**
 * Validates Password Reset inputs.
 */
export function validateNewPassword(password: string, confirm: string): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!password) {
    errors.password = 'New password is required.';
  } else if (password.length < 6) {
    errors.password = 'Password must be at least 6 characters long.';
  }

  if (!confirm) {
    errors.confirmPassword = 'Confirm password is required.';
  } else if (password !== confirm) {
    errors.confirmPassword = 'Passwords do not match.';
  }

  return errors;
}
